import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { getAppAnalyticsSnapshot } from '@/services/appAnalyticsSnapshot';

const fallbackSnapshot = {
  valuation: null,
  valuation_mode: null,
  mrr: null,
  arr: null,
  growth_status: null,
  activation_rate: null,
  content_throughput: null,
  churn_proxy: null,
  system_reliability: null,
  key_drivers: [],
  change_log: []
};

function formatCurrency(value) {
  if (value === null || value === undefined) return '--';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(value);
}

export default function AdminValuation() {
  const [snapshot, setSnapshot] = useState(fallbackSnapshot);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('ADMIN VALUATION ACTIVE: frontend/src/pages/new-app/AdminValuation.jsx');
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const data = await getAppAnalyticsSnapshot();
        if (isMounted) setSnapshot(data || fallbackSnapshot);
      } catch (err) {
        console.error('Failed to load valuation snapshot:', err);
        if (isMounted) {
          setSnapshot(fallbackSnapshot);
          setError('Unable to load valuation data right now.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  const valuationModeLabel =
    snapshot?.valuation_mode || (snapshot?.mrr ? 'Post-revenue MRR-based' : 'Pre-revenue signals');

  const keyDrivers =
    snapshot?.key_drivers?.length
      ? snapshot.key_drivers
      : [
          'Revenue quality (MRR stability, ARPU)',
          'Growth signals (seat velocity / conversion stability)',
          'Retention proxies (repeat usage)',
          'System reliability (health/process stability)'
        ];

  const changeLog = snapshot?.change_log?.length
    ? snapshot.change_log
    : ['No recent valuation changes logged.'];

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-5xl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-1">Valuation</h1>
        <p className="text-[#8A93A6]">Read-only governance valuation summary</p>
      </div>

      <Alert className="bg-pink-500/10 border-pink-500/30 mb-6">
        <AlertDescription className="text-pink-200 text-sm">
          Read-only snapshot. No formulas or automation are exposed on this page.
          {error ? ` (${error})` : ''}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="glass lg:col-span-2">
          <CardHeader>
            <CardTitle>Valuation Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <p className="text-sm text-[#8A93A6]">Headline valuation / proxy</p>
                <p className="text-4xl font-bold text-white">{formatCurrency(snapshot?.valuation)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-[#8A93A6]">Mode</p>
                <Badge variant="outline" className="text-white border-white/20">
                  {valuationModeLabel || '--'}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle>Reliability</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-[#8A93A6] mb-1">System reliability</p>
            <p className="text-2xl font-semibold text-white">{snapshot?.system_reliability || '--'}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Key Drivers</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc list-inside space-y-2 text-sm text-[#8A93A6]">
              {keyDrivers.map((driver, idx) => (
                <li key={idx} className="text-white/80">
                  {driver}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <CardTitle>Change Log</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-[#8A93A6]">
              {changeLog.map((entry, idx) => (
                <li key={idx} className="text-white/80">
                  {entry}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="glass">
        <CardHeader>
          <CardTitle>Inputs (values only)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[#8A93A6]">MRR</p>
              <p className="text-white text-lg font-semibold">
                {snapshot?.mrr ? formatCurrency(snapshot.mrr) : '--'}
              </p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[#8A93A6]">ARR</p>
              <p className="text-white text-lg font-semibold">
                {snapshot?.arr ? formatCurrency(snapshot.arr) : '--'}
              </p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[#8A93A6]">Growth / Flywheel</p>
              <p className="text-white text-lg font-semibold">{snapshot?.growth_status || '--'}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[#8A93A6]">Activation rate</p>
              <p className="text-white text-lg font-semibold">{snapshot?.activation_rate ?? '--'}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[#8A93A6]">Content throughput</p>
              <p className="text-white text-lg font-semibold">{snapshot?.content_throughput ?? '--'}</p>
            </div>
            <div className="p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-[#8A93A6]">Churn proxy</p>
              <p className="text-white text-lg font-semibold">{snapshot?.churn_proxy ?? '--'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
